import { useState, useEffect } from 'react';
import { Box, Flex, Text, Badge, Button } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Tooltip, Input } from '@/components/v4';
import { isRequiredRule } from 'utils/eightByEightCatalog';
import { resolveEffectiveStatus } from './EightByEightResolveEffectiveStatus';

const resolveEffectiveVolume = (item, volumeMap, itemsById) => {
    if (isRequiredRule(item) && item.parentId) {
        const parent = itemsById[item.parentId];
        if (parent) return resolveEffectiveVolume(parent, volumeMap, itemsById);
    }
    return volumeMap[item.id] ?? 0;
};

/**
 * LicenceTreeItem
 *
 * Shared row renderer for LicenceTreeBase.
 * Renders name, status toggle, and optionally a volume and/or active input.
 *
 * Props:
 *   hasVolume  - boolean: show editable volume column (entitlements mode)
 *   hasActive  - boolean: show read-only active count column
 *   activeMap  - { [itemID]: number }: active counts per licence
 *   isReadOnly - boolean: disable all interactive controls
 */
const LicenceTreeItem = ({
    item,
    depth,
    statusMap,
    volumeMap,
    activeMap,
    itemsById,
    childrenOf,
    collapsed,
    onToggle,
    onVolumeChange,
    onToggleCollapse,
    darkMode,
    borderColor,
    rowBg,
    textColor,
    search,
    hasVolume,
    hasActive,
    isReadOnly,
}) => {
    const effectiveStatus = resolveEffectiveStatus(item, statusMap, itemsById);
    const isMust = isRequiredRule(item);
    const isEnabled = effectiveStatus === 'Enabled';
    // Any child licence (MUST or CAN) can only be enabled once its parent is
    // enabled. While the parent is off, the child's toggle is non-interactive
    // and hovering shows an "enable the parent first" tooltip with a
    // not-allowed cursor.
    const parentItem = item.parentId ? itemsById[item.parentId] : null;
    const isParentEnabled = parentItem
        ? resolveEffectiveStatus(parentItem, statusMap, itemsById) === 'Enabled'
        : true;
    const isLockedByParent = !isReadOnly && !!parentItem && !isParentEnabled;
    // MUST children always inherit their parent (never directly toggleable);
    // CAN children become interactive only once their parent is enabled.
    const isToggleInteractive = !isMust && !isReadOnly && !isLockedByParent;
    const isVolumeEditable = hasVolume && isEnabled && !isMust && !isReadOnly;
    const effectiveVolume = hasVolume
        ? resolveEffectiveVolume(item, volumeMap, itemsById)
        : 0;
    const activeCount = hasActive ? (activeMap?.[item.id] ?? 0) : 0;

    // Local display state for volume — allows clearing to '' while editing
    const [rawVolume, setRawVolume] = useState(String(effectiveVolume));
    useEffect(() => {
        setRawVolume(String(effectiveVolume));
    }, [effectiveVolume]);

    const handleVolumeChange = (e) => {
        const val = e.target.value;
        if (val !== '' && !/^\d+$/.test(val)) return;
        setRawVolume(val);
        onVolumeChange(item, val === '' ? 0 : parseInt(val, 10));
    };

    const matchesSearch = (i) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            i.name.toLowerCase().includes(q) ||
            i.description?.toLowerCase().includes(q)
        );
    };
    const hasDescendantMatch = (parentId) =>
        (childrenOf[parentId] || []).some(
            (c) => matchesSearch(c) || hasDescendantMatch(c.id),
        );

    const isSearchActive = !!search;
    const allChildren = childrenOf[item.id] || [];
    const children = isSearchActive
        ? allChildren.filter(
              (c) => matchesSearch(c) || hasDescendantMatch(c.id),
          )
        : allChildren;
    const hasChildren = children.length > 0;
    const isCollapsed = isSearchActive ? false : collapsed[item.id];
    const descColor = '#757575';

    return (
        <Box>
            <Flex
                align="center"
                px={4}
                py={2}
                pl={`${16 + depth * 24}px`}
                bg={rowBg}
                borderBottomWidth="1px"
                borderColor={borderColor}
                opacity={!isEnabled ? 0.5 : 1}
                _hover={{ bg: darkMode ? 'gray.750' : 'gray.50' }}>
                {/* Left: expand toggle + name + badge */}
                <Flex align="flex-start" flex="1" gap={2} mr={4}>
                    {hasChildren ? (
                        <Box
                            as="button"
                            type="button"
                            onClick={() => onToggleCollapse(item.id)}
                            color={textColor}
                            mt="2px"
                            flexShrink={0}
                            cursor="pointer">
                            {isCollapsed ? (
                                <ChevronRightIcon boxSize={4} />
                            ) : (
                                <ChevronDownIcon boxSize={4} />
                            )}
                        </Box>
                    ) : (
                        <Box w={4} flexShrink={0} />
                    )}
                    <Box>
                        <Flex align="center" gap={2}>
                            <Text
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}>
                                {item.name}
                            </Text>
                            {isMust && (
                                <Badge
                                    colorScheme="messenger"
                                    fontSize="9px"
                                    textTransform="none">
                                    Required
                                </Badge>
                            )}
                        </Flex>
                        {item.description && (
                            <Text
                                fontSize="11px"
                                color={descColor}
                                mt="1px"
                                maxW="420px"
                                noOfLines={2}>
                                {item.description}
                            </Text>
                        )}
                    </Box>
                </Flex>

                {/* Disabled / Enabled toggle */}
                <Tooltip
                    label={`Enable “${parentItem?.name ?? 'the parent licence'}” first`}
                    isDisabled={!isLockedByParent}
                    hasArrow
                    placement="top">
                    <Flex
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="md"
                        overflow="hidden"
                        flexShrink={0}
                        mr={hasVolume || hasActive ? 3 : 0}
                        opacity={
                            isMust || isReadOnly || isLockedByParent ? 0.5 : 1
                        }
                        // Keep the row hoverable when locked by parent so the
                        // tooltip + not-allowed cursor show; other
                        // non-interactive rows ignore pointer events as before.
                        pointerEvents={
                            !isLockedByParent && (isMust || isReadOnly)
                                ? 'none'
                                : 'auto'
                        }
                        cursor={isLockedByParent ? 'not-allowed' : undefined}>
                        <Button
                            size="sm"
                            variant="ghost"
                            borderRadius={0}
                            px={4}
                            fontWeight="500"
                            fontSize="xs"
                            // Let the wrapper own hover/cursor when locked so the
                            // tooltip shows reliably and clicks do nothing.
                            pointerEvents={
                                isLockedByParent ? 'none' : undefined
                            }
                            bg={!isEnabled ? 'gray.500' : 'transparent'}
                            color={!isEnabled ? 'white' : textColor}
                            _hover={{
                                bg: !isEnabled ? 'gray.600' : 'gray.100',
                            }}
                            onClick={() =>
                                isToggleInteractive &&
                                onToggle(item, 'Disabled')
                            }>
                            Disabled
                        </Button>
                        <Box w="1px" bg={borderColor} />
                        <Button
                            size="sm"
                            variant="ghost"
                            borderRadius={0}
                            px={4}
                            fontWeight="500"
                            fontSize="xs"
                            pointerEvents={
                                isLockedByParent ? 'none' : undefined
                            }
                            bg={isEnabled ? 'messenger.500' : 'transparent'}
                            color={isEnabled ? 'white' : textColor}
                            _hover={{
                                bg: isEnabled ? 'messenger.600' : 'gray.100',
                            }}
                            onClick={() =>
                                isToggleInteractive && onToggle(item, 'Enabled')
                            }>
                            Enabled
                        </Button>
                    </Flex>
                </Tooltip>

                {/* Volume column — entitlements mode only */}
                {hasVolume && (
                    <Box w="120px" flexShrink={0} mr={hasActive ? 3 : 0}>
                        <Tooltip
                            label="Inherited volume locked by hierarchy"
                            isDisabled={isVolumeEditable}
                            hasArrow
                            placement="top">
                            <Input
                                size="sm"
                                type="text"
                                inputMode="numeric"
                                textAlign="center"
                                w="100px"
                                borderRadius="md"
                                value={rawVolume}
                                onChange={handleVolumeChange}
                                isDisabled={!isVolumeEditable}
                                bg={darkMode ? 'dark.bgDark' : undefined}
                                color={darkMode ? 'dark.textDark' : undefined}
                                borderColor={darkMode ? borderColor : undefined}
                                _disabled={
                                    darkMode
                                        ? { bg: 'dark.bgDark', opacity: 0.4 }
                                        : undefined
                                }
                                px={2}
                            />
                        </Tooltip>
                    </Box>
                )}

                {/* Active column — read-only, manage licences mode only */}
                {hasActive && (
                    <Box w="120px" flexShrink={0} opacity={0.5}>
                        <Input
                            size="sm"
                            type="text"
                            textAlign="center"
                            w="100px"
                            value={activeCount}
                            isReadOnly
                            border="none"
                            _focus={{ boxShadow: 'none' }}
                            _readOnly={{ bg: 'transparent' }}
                            cursor="default"
                            bg="transparent"
                            color={darkMode ? 'dark.textDark' : undefined}
                            px={2}
                        />
                    </Box>
                )}
            </Flex>

            {/* Children */}
            {hasChildren && !isCollapsed && (
                <Box>
                    {children.map((child) => (
                        <LicenceTreeItem
                            key={child.id}
                            item={child}
                            depth={depth + 1}
                            statusMap={statusMap}
                            volumeMap={volumeMap}
                            activeMap={activeMap}
                            itemsById={itemsById}
                            childrenOf={childrenOf}
                            collapsed={collapsed}
                            onToggle={onToggle}
                            onVolumeChange={onVolumeChange}
                            onToggleCollapse={onToggleCollapse}
                            darkMode={darkMode}
                            borderColor={borderColor}
                            rowBg={rowBg}
                            textColor={textColor}
                            search={search}
                            hasVolume={hasVolume}
                            hasActive={hasActive}
                            isReadOnly={isReadOnly}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default LicenceTreeItem;
